import { useEffect, useState } from 'react';
import { getRun } from '../api.ts';
import type { RunCheckpoint, RunDetail, RunJob, StageProgress } from '../types.ts';
import { navigate } from '../lib/router.ts';

const POLL_MS = 2000;
const TICK_MS = 1000;
const TERMINAL: RunDetail['status'][] = ['done', 'failed'];
/** Mirrors MAX_ATTEMPTS in apps/server/src/config.ts — only ever shown, never enforced here. */
const MAX_ATTEMPTS = 3;

/**
 * What a `queued` run is waiting for. The distinction matters to the reader: the first queue is
 * "nothing has gone wrong, the worker is about to pick this up", and a later one is "the previous
 * attempt died and this is the retry" — rendering both as a bare "queued" makes a failing run
 * look like a fresh one.
 */
export function queuedLabel(run: RunDetail): string {
  const attempt = run.attempt ?? 0;
  if (attempt <= 0) return 'queued — the worker picks it up within seconds';
  return `queued — retrying (attempt ${attempt + 1} of ${MAX_ATTEMPTS})`;
}

/**
 * What a terminal run with a report credit owes the reader (docs/specs/degraded-runs.md). Two
 * shapes, because the two cases are genuinely different: a failed run delivered nothing, while a
 * degraded one delivered a comparison that is missing named vendors. Both refund; only one has an
 * artifact to go and read.
 */
export function refundLine(run: Pick<RunDetail, 'status' | 'competitors'>): string {
  const refunded = 'This run does not count toward your monthly runs.';
  if (run.status === 'failed') return `This run did not deliver a comparison. ${refunded}`;
  const failed = run.competitors.filter((c) => c.status === 'failed').length;
  const total = run.competitors.length;
  return (
    `${failed} of ${total} competitors could not be assessed — they appear in the artifact as ` +
    `columns of unknown, not as gaps in the market. ${refunded}`
  );
}

/** Checkpointed stages, keyed for the per-competitor stage table. */
export function checkpointsByStage(
  checkpoints: RunCheckpoint[] | undefined,
  competitor: string,
): Map<string, RunCheckpoint> {
  const out = new Map<string, RunCheckpoint>();
  for (const c of checkpoints ?? []) {
    if (c.competitor === competitor) out.set(c.stage, c);
  }
  return out;
}

/**
 * A duration, in the shortest form that stays unambiguous: seconds under a minute, `m:ss` under an
 * hour, `h:mm` beyond. Never a bare number — "94" is not a duration, and every time in this view
 * is data the reader may want to check, so it is set in the mono face (docs/design-system.md,
 * "Mono means verifiable").
 */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const total = Math.round(ms / 1000);
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  if (minutes < 60) return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
}

/**
 * How long something that started at `startedAt` has been running. Null when we were never told
 * when it started — the view then shows nothing rather than a zero, because "0s" and "we don't
 * know" are different claims and only one of them is true.
 */
export function elapsedMs(startedAt: string | null | undefined, nowMs: number): number | null {
  if (!startedAt) return null;
  const started = Date.parse(startedAt);
  if (Number.isNaN(started)) return null;
  return Math.max(0, nowMs - started);
}

/**
 * The duration to show for one unit of work: its recorded `ms` once it has finished, and a live
 * clock against `started_at` while it runs. A finished unit never ticks — its duration is a fact,
 * not an observation.
 */
export function unitDuration(
  unit: { status: string; started_at?: string | null; ms?: number | null },
  nowMs: number,
): string | null {
  if (unit.status === 'running') {
    const live = elapsedMs(unit.started_at, nowMs);
    return live == null ? null : formatDuration(live);
  }
  if (unit.ms != null && unit.ms > 0) return formatDuration(unit.ms);
  return null;
}

/** A clock that only runs while it is needed. A terminal run's elapsed time is settled, and
 * re-rendering it every second would be a second-by-second lie about it changing. */
function useNow(active: boolean): number {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => setNowMs(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, [active]);
  return nowMs;
}

const money = (usd: number | null | undefined): string | null =>
  usd == null ? null : `$${usd.toFixed(2)}`;

/** Run view (docs/specs/app.md, apps/web §2): poll every 2s, per-competitor per-stage progress,
 * plus the run-level jobs — compare, the demand lanes, the demand assignment.
 *
 * `showCost` is the viewer's role, reduced to the one thing this view needs from it: a non-admin
 * never sees a dollar figure (docs/specs/accounts.md, section 5). */
export function RunView({
  sessionId,
  runId,
  showCost,
}: {
  sessionId: string;
  runId: string;
  showCost: boolean;
}): JSX.Element {
  const [run, setRun] = useState<RunDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const live = run != null && !TERMINAL.includes(run.status);
  const nowMs = useNow(live);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    async function poll(): Promise<void> {
      try {
        const r = await getRun(runId);
        if (cancelled) return;
        setRun(r);
        setError(null);
        if (!TERMINAL.includes(r.status)) {
          timer = setTimeout(poll, POLL_MS);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          timer = setTimeout(poll, POLL_MS);
        }
      }
    }
    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [runId]);

  return (
    <RunReadout
      sessionId={sessionId}
      runId={runId}
      run={run}
      error={error}
      nowMs={nowMs}
      showCost={showCost}
    />
  );
}

/**
 * Everything the run view draws, given a run. Apart from the polling above so that it renders
 * from a plain object — which is what lets a test hold the cost rule to account. With `showCost`
 * false every cost column goes, header included: an empty cost cell would still say "there is a
 * cost here", and "—" already means "not tracked" (docs/design-system.md, "Cost is an admin
 * readout").
 */
export function RunReadout({
  sessionId,
  runId,
  run,
  error,
  nowMs,
  showCost,
}: {
  sessionId: string;
  runId: string;
  run: RunDetail | null;
  error: string | null;
  nowMs: number;
  showCost: boolean;
}): JSX.Element {
  const live = run != null && !TERMINAL.includes(run.status);
  // A live run's elapsed time runs from its start to now; a finished one's runs to its last
  // update, which is the moment it stopped doing anything.
  const runElapsed = run
    ? elapsedMs(run.started_at, live ? nowMs : Date.parse(run.updated_at))
    : null;
  const demandJobs = (run?.jobs ?? []).filter((j) => j.group === 'demand');
  const compareJobs = (run?.jobs ?? []).filter((j) => j.group === 'compare');

  return (
    <div className="run-view">
      <header className="run-masthead">
        <a className="run-back bare" href={`#/sessions/${sessionId}`}>
          ← back to chat
        </a>
        <div className="run-title-row">
          <h2 className="display run-title">
            Run <span className="mono">{runId}</span>
          </h2>
          {run && <span className={`key status-${run.status}`}>{run.status}</span>}
        </div>
        {run && (
          <div className="run-meta">
            {showCost && run.cost_usd != null && <span>cost so far ${run.cost_usd.toFixed(2)}</span>}
            {runElapsed != null && <span>elapsed {formatDuration(runElapsed)}</span>}
            <span>started {run.started_at}</span>
            <span>updated {run.updated_at}</span>
          </div>
        )}
        {run?.status === 'queued' && <p className="run-queued">{queuedLabel(run)}</p>}
        {/* Only once the run has stopped: a refund on a run still in flight would be a promise
            about an outcome nobody has yet (docs/specs/degraded-runs.md). */}
        {run && TERMINAL.includes(run.status) && run.report_credit === 'refund_due' && (
          <p className="run-refund">{refundLine(run)}</p>
        )}
      </header>

      {error && <p className="error">{error}</p>}
      {run?.error && <p className="error">{run.error}</p>}
      {!run && !error && (
        <div className="panel grat run-loading">
          <span className="label">loading…</span>
        </div>
      )}

      {run && (
        <section className="run-section">
          <div className="tick">
            <span className="label">§ Competitors</span>
          </div>
          <div className="hairline-stack">
            {run.competitors.map((c) => {
              const checkpointed = checkpointsByStage(run.checkpoints, c.slug);
              return (
                <div key={c.slug} className="comp-row">
                  <div className="comp-row-head">
                    <span className="comp-name">{c.company}</span>
                    <span className={`key status-${c.status}`}>{c.status}</span>
                    {showCost && c.cost_usd != null && <span className="chip">${c.cost_usd.toFixed(2)}</span>}
                  </div>
                  {c.error && <p className="error">{c.error}</p>}
                  <div className="scroll-x">
                    <table className="stage-table">
                      <thead>
                        <tr>
                          <th>
                            <span className="label">stage</span>
                          </th>
                          <th>
                            <span className="label">status</span>
                          </th>
                          <th>
                            <span className="label">pages</span>
                          </th>
                          <th>
                            <span className="label">time</span>
                          </th>
                          {showCost && (
                            <th>
                              <span className="label">cost</span>
                            </th>
                          )}
                          <th>
                            <span className="label">error</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {c.stages.map((s) => (
                          <StageRow
                            key={s.stage}
                            stage={s}
                            cp={checkpointed.get(s.stage)}
                            nowMs={nowMs}
                            showCost={showCost}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}

            {/* compare, as a job when the server sends one, and as a bare status for runs from
                before the jobs list existed */}
            {compareJobs.length > 0 ? (
              <JobRow job={compareJobs[0]!} nowMs={nowMs} showCost={showCost} />
            ) : (
              run.compare_status && (
                <div className="comp-row">
                  <div className="comp-row-head">
                    <span className="comp-name">compare</span>
                    <span className={`key status-${run.compare_status}`}>{run.compare_status}</span>
                  </div>
                </div>
              )
            )}
          </div>
        </section>
      )}

      {demandJobs.length > 0 && (
        <section className="run-section">
          <div className="tick">
            <span className="label">§ Demand lanes</span>
          </div>
          <div className="hairline-stack">
            <div className="comp-row">
              <div className="comp-row-head">
                <span className="comp-name">signal mining</span>
                {showCost && run?.demand_cost_usd != null && (
                  <span className="chip">${run.demand_cost_usd.toFixed(2)}</span>
                )}
              </div>
              <div className="scroll-x">
                <table className="stage-table">
                  <thead>
                    <tr>
                      <th>
                        <span className="label">lane</span>
                      </th>
                      <th>
                        <span className="label">status</span>
                      </th>
                      <th>
                        <span className="label">found</span>
                      </th>
                      <th>
                        <span className="label">time</span>
                      </th>
                      {showCost && (
                        <th>
                          <span className="label">cost</span>
                        </th>
                      )}
                      <th>
                        <span className="label">note</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {demandJobs.map((job) => (
                      <LaneRow key={job.stage} job={job} nowMs={nowMs} showCost={showCost} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      )}

      {run?.status === 'done' && (
        <button
          className="btn primary run-cta"
          onClick={() => navigate({ view: 'artifact', sessionId, runId })}
        >
          View artifact →
        </button>
      )}
    </div>
  );
}

function StageRow({
  stage,
  cp,
  nowMs,
  showCost,
}: {
  stage: StageProgress;
  cp: RunCheckpoint | undefined;
  nowMs: number;
  showCost: boolean;
}): JSX.Element {
  const duration = unitDuration(stage, nowMs);
  return (
    <tr>
      <td className="mono">{stage.stage}</td>
      <td>
        <span className={`key status-${stage.status}`}>{stage.status}</span>
        {/* a stage this attempt skipped: say whose work it was, rather than letting a resumed run
            look like it did everything itself */}
        {cp && (
          <span className="from-attempt">
            from attempt {cp.from_attempt + 1}
            {showCost && cp.cost_usd != null && ` · $${cp.cost_usd.toFixed(2)}`}
            {cp.ms > 0 && ` · ${formatDuration(cp.ms)}`}
          </span>
        )}
      </td>
      <td className="mono">{stage.pages_fetched}</td>
      <td className="mono">{cp ? formatDuration(cp.ms) : (duration ?? '')}</td>
      {showCost && <td className="mono">{money(cp ? cp.cost_usd : stage.cost_usd) ?? ''}</td>}
      <td className="error">{stage.error ?? ''}</td>
    </tr>
  );
}

/** One demand lane. `found` is the live triage count — searches, fetches, kept threads, signals —
 * which is the only thing that moves during the minutes a lane spends inside its finder loop. */
function LaneRow({
  job,
  nowMs,
  showCost,
}: {
  job: RunJob;
  nowMs: number;
  showCost: boolean;
}): JSX.Element {
  const c = job.counts;
  return (
    <tr>
      <td className="mono">{job.label}</td>
      <td>
        <span className={`key status-${job.status}`}>{job.status}</span>
        {job.from_attempt != null && (
          <span className="from-attempt">from attempt {job.from_attempt + 1}</span>
        )}
      </td>
      <td className="mono">
        {c ? `${c.searches}s · ${c.fetches}f · ${c.kept}k · ${c.signals} sig` : ''}
      </td>
      <td className="mono">{unitDuration(job, nowMs) ?? ''}</td>
      {showCost && <td className="mono">{money(job.cost_usd) ?? ''}</td>}
      <td className="lane-note">{job.error ?? job.summary ?? ''}</td>
    </tr>
  );
}

/** Compare, rendered in the competitor stack with the same head treatment as a competitor row. */
function JobRow({
  job,
  nowMs,
  showCost,
}: {
  job: RunJob;
  nowMs: number;
  showCost: boolean;
}): JSX.Element {
  const duration = unitDuration(job, nowMs);
  return (
    <div className="comp-row">
      <div className="comp-row-head">
        <span className="comp-name">{job.label}</span>
        <span className={`key status-${job.status}`}>{job.status}</span>
        {duration && <span className="job-time">{duration}</span>}
        {showCost && job.cost_usd != null && <span className="chip">${job.cost_usd.toFixed(2)}</span>}
      </div>
      {job.error && <p className="error">{job.error}</p>}
    </div>
  );
}
